import React, { useState } from 'react';
import { UserProfile, UserSubscription } from '../types';
import { AppSettings } from '../lib/storage';
import { StorePaymentSheet } from './StorePaymentSheet';
import {
  Crown,
  Sparkles,
  Palette,
  ShieldCheck,
  Zap,
  Image as ImageIcon,
  FileUp,
  MessageSquareCode,
  BellRing,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ArrowRight,
  Gift,
  RefreshCw,
  Clock,
  ExternalLink,
  ChevronLeft,
  Sliders,
  Check,
  Star,
  Layers,
  Smartphone,
  CreditCard,
  AlertTriangle,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

interface PremiumViewProps {
  currentUser: UserProfile;
  appSettings?: AppSettings;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
  onUpdateSettings?: (settings: Partial<AppSettings>) => void;
  onCloseModal?: () => void;
}

export const PremiumView: React.FC<PremiumViewProps> = ({
  currentUser,
  appSettings,
  onUpdateProfile,
  onUpdateSettings,
  onCloseModal,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('تجربة الخدمة فقط');

  // Platform Store Sheet state (Apple Pay / Google Play / Cards)
  const [showStoreSheet, setShowStoreSheet] = useState(false);
  const [sheetPlan, setSheetPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [sheetIsTrial, setSheetIsTrial] = useState(true);

  const sub = currentUser.subscription;
  const isPremiumActive = currentUser.isPremium && sub?.status && ['active', 'trialing', 'canceled', 'in_grace_period'].includes(sub.status);
  const isTrial = sub?.status === 'trialing';
  const isCanceledAutoRenew = sub?.status === 'canceled';

  // Open the official store payment sheet with chosen plan & trial
  const openStoreSheet = (plan: 'monthly' | 'yearly', isTrialOption: boolean) => {
    soundEffects.playTap();
    setSheetPlan(plan);
    setSheetIsTrial(isTrialOption);
    setShowStoreSheet(true);
  };

  // Called ONLY when the platform store verifies the transaction token on the server
  const handlePurchaseSuccess = (verifiedSub: UserSubscription) => {
    soundEffects.playCrown();
    setFeedbackMsg({
      type: 'success',
      text: sheetIsTrial
        ? '🎉 مبروك! تم التحقق من إيصال الشراء بنجاح عبر المتجر وتفعيل الشهر المجاني 100% في وتين Premium 👑!'
        : '🎉 مبروك! تم التحقق من إيصال الشراء بنجاح عبر المتجر وتفعيل اشتراك وتين Premium 👑!',
    });

    onUpdateProfile({
      isPremium: true,
      subscription: verifiedSub,
      chatBubbleStyle: currentUser.chatBubbleStyle || 'royal_gold',
    });
  };

  // Quick action: Cancel Auto Renew
  const handleCancelAutoRenew = async () => {
    setIsProcessing(true);
    setFeedbackMsg(null);
    soundEffects.playTap();

    try {
      const res = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          reason: cancelReason,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'فشل إيقاف التجديد');

      setShowCancelModal(false);
      setFeedbackMsg({
        type: 'success',
        text: 'تم إيقاف التجديد التلقائي بنجاح. ستستمر مزاياك حتى نهاية الفترة الحالية ولن يتم خصم أي مبالغ.',
      });

      onUpdateProfile({
        subscription: data.subscription,
      });
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message || 'حدث خطأ أثناء محاولة الإلغاء' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Simulate server-to-server platform store events (for verification & full testing)
  const handleSimulateStoreEvent = async (
    eventType: 'renewed' | 'canceled' | 'expired' | 'refunded' | 'billing_issue'
  ) => {
    setIsProcessing(true);
    setFeedbackMsg(null);
    soundEffects.playTap();

    try {
      const res = await fetch('/api/subscription/simulate-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          eventType,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'فشلت معالجة حدث المتجر');

      soundEffects.playTap();
      setFeedbackMsg({
        type: 'success',
        text: data.message || 'تم تحديث حالة الاشتراك بنجاح وفق استجابة المتجر.',
      });

      onUpdateProfile({
        isPremium: Boolean(data.subscription?.isPremium),
        subscription: data.subscription,
      });
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message || 'فشلت معالجة الحدث' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Quick action: Restore or Renew
  const handleRestore = async () => {
    setIsProcessing(true);
    setFeedbackMsg(null);
    soundEffects.playTap();

    try {
      const res = await fetch('/api/subscription/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'فشلت استعادة الاشتراك');

      soundEffects.playCrown();
      setFeedbackMsg({ type: 'success', text: 'تمت استعادة حالة اشتراكك بنجاح!' });
      onUpdateProfile({
        isPremium: Boolean(data.subscription?.isPremium),
        subscription: data.subscription,
      });
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message || 'تعذرت الاستعادة حالياً' });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'غير محدد';
    const d = new Date(timestamp);
    return d.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-full bg-[#080d16] text-slate-100 p-3 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto font-['Cairo'] select-none">
      {/* Top Banner / Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-950/40 via-[#0e1422] to-emerald-950/30 border border-amber-500/30 p-6 sm:p-8 shadow-2xl">
        {/* Background ambient decorative glow */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-60 h-60 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-bold tracking-wide">
              <Crown className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span>الاشتراك الاختياري الاحترافي</span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight flex items-center gap-3">
              <span>وتين Premium</span>
              <span className="text-amber-400 text-3xl sm:text-4xl">👑</span>
            </h1>

            <p className="text-base sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-emerald-200 to-teal-100">
              “خلّي تجربتك مع وتين أفضل.”
            </p>

            <p className="text-xs sm:text-sm text-slate-300 max-w-xl leading-relaxed">
              تطبيق وتين يظل <strong className="text-emerald-400 font-bold">مجانياً 100% دائماً</strong> للمراسلة وإنشاء الحسابات ومشاركة الوسائط الأساسية. وتين Premium يوفر مزايا حصرية راقية للأشخاص الذين يبحثون عن أقصى درجات التميز والجودة.
            </p>
          </div>

          {/* Quick badge / status indicator */}
          <div className="shrink-0 w-full md:w-auto flex flex-col sm:flex-row md:flex-col gap-2.5">
            {isPremiumActive ? (
              <div className="p-4 rounded-2xl bg-amber-500/15 border border-amber-500/40 text-center space-y-1">
                <div className="flex items-center justify-center gap-1.5 text-amber-300 text-xs font-black">
                  <Crown className="w-4 h-4 fill-current" />
                  <span>عضوية Premium نشطة</span>
                </div>
                <div className="text-[11px] text-slate-300 font-medium">
                  {isTrial ? 'فترة تجريبية مجانية 🎁' : sub?.plan === 'yearly' ? 'اشتراك سنوي 👑' : 'اشتراك شهري 👑'}
                </div>
                <div className="text-[10px] text-amber-200/80 font-mono">
                  ينتهي في: {formatDate(sub?.expiresAt)}
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-500/30 text-center space-y-1">
                <div className="text-xs font-bold text-emerald-300 flex items-center justify-center gap-1">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <span>عرض حصري للمستخدمين الجدد</span>
                </div>
                <div className="text-sm font-black text-white">أول شهر مجاني 100% 🎁</div>
                <div className="text-[10px] text-slate-400">ثم $0.99/شهر فقط، إلغاء في أي وقت</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feedback message banner */}
      {feedbackMsg && (
        <div
          className={`p-4 rounded-2xl border flex items-center justify-between text-xs sm:text-sm font-bold animate-in fade-in duration-200 ${
            feedbackMsg.type === 'success'
              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
              : 'bg-rose-500/15 border-rose-500/40 text-rose-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {feedbackMsg.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-rose-400 shrink-0" />
            )}
            <span>{feedbackMsg.text}</span>
          </div>
          <button
            onClick={() => setFeedbackMsg(null)}
            className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}

      {/* ========================================================= */}
      {/* IF USER ALREADY HAS PREMIUM: MANAGEMENT & CUSTOMIZATION */}
      {/* ========================================================= */}
      {isPremiumActive && (
        <div className="space-y-6">
          {/* Subscription details card */}
          <div className="p-6 rounded-3xl bg-[#0e1422] border border-amber-500/30 space-y-4 shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 shadow-inner">
                  <Crown className="w-6 h-6 fill-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <span>إدارة اشتراكك في وتين Premium</span>
                    <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold border border-amber-500/40">
                      👑 VIP
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    يمكنك متابعة وتخصيص اشتراكك أو إيقاف التجديد التلقائي بكل شفافية
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleRestore}
                  disabled={isProcessing}
                  className="px-3.5 py-1.5 rounded-xl bg-[#141c2a] hover:bg-[#1e293b] border border-slate-700 text-xs font-bold text-slate-300 hover:text-white transition flex items-center gap-1.5"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`} />
                  <span>تحديث الحالة</span>
                </button>
              </div>
            </div>

            {/* Information Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="p-3.5 rounded-2xl bg-[#141b27] border border-slate-800">
                <div className="text-slate-400 text-[11px] mb-1">نوع الخطة الحالية</div>
                <div className="font-bold text-white flex items-center gap-1.5">
                  <span>{isTrial ? 'فترة تجريبية مجانية 🎁' : sub?.plan === 'yearly' ? 'سنوية ($8.99/سنة)' : 'شهرية ($0.99/شهر)'}</span>
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#141b27] border border-slate-800">
                <div className="text-slate-400 text-[11px] mb-1">تاريخ التجديد القادم</div>
                <div className="font-bold text-emerald-400 font-mono">
                  {formatDate(sub?.expiresAt)}
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#141b27] border border-slate-800">
                <div className="text-slate-400 text-[11px] mb-1">حالة التجديد التلقائي</div>
                <div className="font-bold flex items-center gap-1.5">
                  {isCanceledAutoRenew ? (
                    <span className="text-rose-400">ملغي (ينتهي دون خصم)</span>
                  ) : (
                    <span className="text-emerald-400 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" /> مفعل تلقائياً
                    </span>
                  )}
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#141b27] border border-slate-800">
                <div className="text-slate-400 text-[11px] mb-1">معرّف المعاملة المعتمَد</div>
                <div className="font-mono text-[10px] text-slate-300 truncate" title={sub?.transactionId}>
                  {sub?.transactionId || 'tx_wateen_official'}
                </div>
              </div>
            </div>

            {/* Auto Renew Control notice */}
            <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
              <p className="text-slate-400 leading-relaxed text-[11px]">
                {isCanceledAutoRenew
                  ? '⚠️ قمت بإيقاف التجديد التلقائي مسبقاً. ستستمر كافة مزاياك حتى نهاية الفترة دون أي انقطاع، ولن يتم خصم أي مبالغ.'
                  : 'تستطيع إيقاف التجديد التلقائي في أي وقت قبل تاريخ الانتهاء، وسيظل حسابك ومراسلاتك آمنة دون حذف أي بيانات.'}
              </p>

              {isCanceledAutoRenew ? (
                <button
                  type="button"
                  onClick={() => openStoreSheet(sub?.plan || 'monthly', false)}
                  disabled={isProcessing}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-xs hover:from-emerald-400 hover:to-teal-500 transition shadow shrink-0"
                >
                  إعادة تفعيل التجديد عبر المتجر ✓
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 text-xs font-bold transition shrink-0"
                >
                  إيقاف التجديد التلقائي
                </button>
              )}
            </div>

            {/* Platform Store Lifecycle Testing Suite (Simulating real App Store / Google Play webhooks) */}
            <div className="pt-3 border-t border-slate-800 space-y-2">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-bold text-slate-300 flex items-center gap-1">
                  <Smartphone className="w-3.5 h-3.5 text-indigo-400" />
                  <span>محاكاة دورة حياة اشتراك المتجر (Store Lifecycle Testing):</span>
                </span>
                <span className="text-slate-500 text-[10px]">
                  متجر: {sub?.platformStore === 'apple_app_store' ? ' App Store' : 'Google Play'}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 text-[10px]">
                <button
                  type="button"
                  onClick={() => handleSimulateStoreEvent('renewed')}
                  disabled={isProcessing}
                  className="p-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-emerald-300 font-bold transition border border-slate-700"
                  title="محاكاة تجديد دوري تلقائي من المتجر وتحديث تاريخ الانتهاء"
                >
                  🔄 محاكاة تجديد دوري
                </button>

                <button
                  type="button"
                  onClick={() => handleSimulateStoreEvent('canceled')}
                  disabled={isProcessing}
                  className="p-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-amber-300 font-bold transition border border-slate-700"
                  title="محاكاة قيام المستخدم بإلغاء التجديد من إعدادات Apple ID / Google Play"
                >
                  🚫 محاكاة إلغاء المتجر
                </button>

                <button
                  type="button"
                  onClick={() => handleSimulateStoreEvent('billing_issue')}
                  disabled={isProcessing}
                  className="p-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-yellow-300 font-bold transition border border-slate-700"
                  title="محاكاة تعثر في سداد البطاقة البنكية ودخول فترة السماح"
                >
                  ⚠️ تعثر بطاقة (Grace)
                </button>

                <button
                  type="button"
                  onClick={() => handleSimulateStoreEvent('refunded')}
                  disabled={isProcessing}
                  className="p-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-rose-300 font-bold transition border border-slate-700"
                  title="محاكاة طلب المستخدم استرداد نقدي (Refund) من Apple / Google وإلغاء المزايا فوراً"
                >
                  💸 محاكاة استرداد نقدي
                </button>

                <button
                  type="button"
                  onClick={() => handleSimulateStoreEvent('expired')}
                  disabled={isProcessing}
                  className="p-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 font-bold transition border border-slate-700"
                  title="محاكاة انتهاء الصلاحية وإعادة الحساب للخطة المجانية"
                >
                  ⌛ محاكاة انتهاء الصلاحية
                </button>
              </div>
            </div>
          </div>

          {/* ======================================================== */}
          {/* EXCLUSIVE PREMIUM CUSTOMIZATION SUITE (CHAT & PROFILE)   */}
          {/* ======================================================== */}
          <div className="p-6 rounded-3xl bg-[#0e1422] border border-slate-800 space-y-6 shadow-xl">
            <div className="pb-3 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-amber-400" />
                  <span>تخصيص مزايا وتين Premium المفتوحة لك</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  تحكم بنمط فقاعة رسائلك، جودة رفع الوسائط، وثيمات المنصة الفاخرة
                </p>
              </div>
              <span className="text-[11px] font-bold text-amber-400 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30">
                متاح فقط لمشتركي Premium
              </span>
            </div>

            {/* 1. Chat Bubble Style */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-white flex items-center gap-1.5">
                  <MessageSquareCode className="w-4 h-4 text-emerald-400" />
                  <span>نمط فقاعة الرسائل الخاصة بك (Message Bubble Style)</span>
                </label>
                <span className="text-[11px] text-slate-400">يظهر لرسائلك في جميع المحادثات</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                {[
                  {
                    id: 'royal_gold',
                    label: 'الذهب الملكي 👑',
                    style: 'bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-600 text-slate-950 font-bold border border-amber-400/50 shadow-amber-500/20 shadow-lg',
                  },
                  {
                    id: 'neon_glow',
                    label: 'الزمرد المتوهج ⚡',
                    style: 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-bold border border-emerald-400/60 shadow-emerald-500/30 shadow-lg',
                  },
                  {
                    id: 'glass_morphism',
                    label: 'الزجاج الكوني 🌌',
                    style: 'bg-indigo-950/60 border border-indigo-400/40 text-indigo-100 backdrop-blur-md shadow-indigo-500/20 shadow-md',
                  },
                  {
                    id: 'midnight_diamond',
                    label: 'الماس الداكن 💎',
                    style: 'bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 border border-purple-400/40 text-purple-100 shadow-purple-500/20 shadow-md',
                  },
                  {
                    id: 'default',
                    label: 'الكلاسيكي القياسي 🌿',
                    style: 'bg-gradient-to-l from-emerald-600 to-teal-700 text-white',
                  },
                ].map((b) => {
                  const isSelected = (currentUser.chatBubbleStyle || 'royal_gold') === b.id;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => {
                        soundEffects.playTap();
                        onUpdateProfile({ chatBubbleStyle: b.id as any });
                      }}
                      className={`p-3 rounded-2xl border text-right transition flex flex-col justify-between gap-2 relative ${
                        isSelected
                          ? 'border-amber-400 bg-amber-500/10 ring-2 ring-amber-400/40'
                          : 'border-slate-800 bg-[#141b27] hover:border-slate-700'
                      }`}
                    >
                      <span className="text-xs font-bold text-white">{b.label}</span>
                      <div className={`p-2 rounded-xl text-[10px] text-center truncate ${b.style}`}>
                        رسالة وتين ✨
                      </div>
                      {isSelected && (
                        <span className="absolute top-2 left-2 w-4 h-4 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-[10px] font-black">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2. Media Quality & Upload Quotas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
              <div className="p-4 rounded-2xl bg-[#141b27] border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-white text-xs flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-emerald-400" />
                    <span>إرسال الصور والفيديو بجودة HD/4K الأصلية</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={currentUser.highQualityMedia ?? true}
                    onChange={(e) => {
                      soundEffects.playTap();
                      onUpdateProfile({ highQualityMedia: e.target.checked });
                    }}
                    className="w-4 h-4 accent-amber-500 rounded"
                  />
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  عند التفعيل، يتم رفع وإرسال صورك وفيديوهاتك بدقتها الكاملة دون أي ضغط خوارزمي.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-[#141b27] border border-slate-800 space-y-2">
                <div className="font-bold text-white text-xs flex items-center gap-2">
                  <FileUp className="w-4 h-4 text-amber-400" />
                  <span>حد الملفات: 500 ميغابايت (MB) مفعل 👑</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  يمكنك رفع ومشاركة ملفات ومقاطع فيديو ومستندات ضخمة حتى 500MB مقارنة بـ 25MB فقط للمستخدمين المجانيين.
                </p>
              </div>
            </div>

            {/* 3. Exclusive Premium Themes Quick Switcher */}
            <div className="pt-2 border-t border-slate-800 space-y-2.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-white flex items-center gap-2">
                  <Palette className="w-4 h-4 text-amber-400" />
                  <span>الثيمات الفاخرة الحصرية لمشتركي Premium</span>
                </label>
                <span className="text-[11px] text-amber-300">4 ثيمات ملكية خاصة</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[
                  {
                    key: 'royal_gold',
                    label: 'الذهب الملكي (Royal Gold)',
                    bg: 'bg-gradient-to-r from-amber-950 via-yellow-900 to-[#121927]',
                    accent: 'border-amber-400',
                  },
                  {
                    key: 'cosmic_nebula',
                    label: 'السديم الكوني (Cosmic Nebula)',
                    bg: 'bg-gradient-to-r from-indigo-950 via-purple-950 to-[#080d16]',
                    accent: 'border-purple-400',
                  },
                  {
                    key: 'neon_emerald',
                    label: 'الزمرد المتوهج (Neon Emerald)',
                    bg: 'bg-gradient-to-r from-emerald-950 via-teal-950 to-[#080d16]',
                    accent: 'border-emerald-400',
                  },
                  {
                    key: 'dark_diamond',
                    label: 'الماس الداكن (Dark Diamond)',
                    bg: 'bg-gradient-to-r from-slate-950 via-zinc-900 to-black',
                    accent: 'border-cyan-400',
                  },
                ].map((th) => {
                  const currentTheme = appSettings?.theme || 'dark';
                  const isThemeSelected = currentTheme === th.key;
                  return (
                    <button
                      key={th.key}
                      type="button"
                      onClick={() => {
                        soundEffects.playTap();
                        onUpdateSettings?.({ theme: th.key as any });
                      }}
                      className={`p-3 rounded-2xl border text-right transition ${
                        isThemeSelected
                          ? `${th.accent} bg-white/5 font-bold shadow-lg`
                          : 'border-slate-800 bg-[#141b27] text-slate-400 hover:text-white'
                      }`}
                    >
                      <div className={`w-full h-8 rounded-xl ${th.bg} border border-white/10 mb-2`} />
                      <span className="text-xs text-white block truncate">{th.label}</span>
                      {isThemeSelected && (
                        <span className="text-[10px] text-amber-400 font-bold block mt-1">
                          الثيم الحالي ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* IF NOT SUBSCRIBED: PLANS SELECTION & TRIAL OFFER          */}
      {/* ========================================================= */}
      {!isPremiumActive && (
        <div className="space-y-6">
          {/* GIFT TRIAL SPOTLIGHT */}
          <div className="p-6 rounded-3xl bg-gradient-to-r from-amber-500/20 via-emerald-500/15 to-teal-500/20 border-2 border-amber-400/50 relative overflow-hidden shadow-2xl">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-amber-500 text-slate-950 flex items-center justify-center text-2xl shadow-lg shrink-0">
                  🎁
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 text-[11px] font-bold mb-1">
                    <span>العرض الخاص للمستخدمين الجدد</span>
                  </div>
                  <h3 className="text-lg font-black text-white">
                    أول شهر مجاني بالكامل (100% Free Trial) 🎁
                  </h3>
                  <p className="text-xs text-slate-300 mt-1 max-w-lg leading-relaxed">
                    جرب كافة مزايا وتين Premium لمدة 30 يوماً بدون أن تدفع أي قرش. يتحول الاشتراك بعد انتهاء الشهر المجاني تلقائياً إلى الاشتراك الشهري ($0.99) ما لم تقم بإلغائه في أي وقت قبل انتهاء الفترة.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => openStoreSheet('monthly', true)}
                disabled={isProcessing}
                className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 text-slate-950 font-black text-xs sm:text-sm hover:scale-[1.02] active:scale-95 transition shadow-lg shadow-amber-500/30 shrink-0 flex items-center justify-center gap-2"
              >
                <Gift className="w-4 h-4" />
                <span>ابدأ شهرك المجاني الآن 🎁</span>
              </button>
            </div>
          </div>

          {/* PRICING PLANS CARDS (Clear & Unambiguous) */}
          <div className="space-y-3">
            <div className="text-center sm:text-right">
              <h2 className="text-lg font-bold text-white">اختر خطة الاشتراك المناسبة لك</h2>
              <p className="text-xs text-slate-400">
                أسعار منخفضة جداً ومناسبة للجميع لضمان وصول المزايا الراقية لكل محب لوتين
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PLAN 1: Monthly with Trial */}
              <div
                onClick={() => setSelectedPlan('monthly')}
                className={`p-6 rounded-3xl border transition cursor-pointer relative flex flex-col justify-between gap-4 ${
                  selectedPlan === 'monthly'
                    ? 'border-amber-400 bg-[#0e1422] ring-2 ring-amber-400/30 shadow-2xl'
                    : 'border-slate-800 bg-[#0c101c] hover:border-slate-700'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/30 flex items-center gap-1">
                      <Gift className="w-3.5 h-3.5" />
                      <span>يتضمن شهر مجاني أولاً 🎁</span>
                    </span>
                    <input
                      type="radio"
                      name="plan"
                      checked={selectedPlan === 'monthly'}
                      onChange={() => setSelectedPlan('monthly')}
                      className="w-4 h-4 accent-amber-400"
                    />
                  </div>

                  <h3 className="text-base font-bold text-white">الاشتراك الشهري (Monthly)</h3>
                  <div className="flex items-baseline gap-1.5 pt-1">
                    <span className="text-3xl font-black text-amber-400 font-mono">$0.99</span>
                    <span className="text-xs text-slate-400">/ شهرياً (بعد انتهاء الشهر المجاني)</span>
                  </div>

                  <p className="text-xs text-slate-300 leading-relaxed pt-2 border-t border-slate-800">
                    أول شهر مجاني 100%، ثم $0.99 شهرياً ما لم يتم الإلغاء قبل انتهاء الفترة التجريبية بـ 24 ساعة عبر إعدادات المتجر.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openStoreSheet('monthly', true);
                  }}
                  disabled={isProcessing}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-bold text-xs transition shadow-md flex items-center justify-center gap-2"
                >
                  <Gift className="w-4 h-4" />
                  <span>اشترك الآن (أول شهر مجاناً) 🎁</span>
                </button>
              </div>

              {/* PLAN 2: Yearly with 25% Discount */}
              <div
                onClick={() => setSelectedPlan('yearly')}
                className={`p-6 rounded-3xl border transition cursor-pointer relative flex flex-col justify-between gap-4 ${
                  selectedPlan === 'yearly'
                    ? 'border-amber-400 bg-[#0e1422] ring-2 ring-amber-400/30 shadow-2xl'
                    : 'border-slate-800 bg-[#0c101c] hover:border-slate-700'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-black border border-amber-500/40 flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-current text-amber-400" />
                      <span>القيمة الأفضل (وفر 25%) 👑</span>
                    </span>
                    <input
                      type="radio"
                      name="plan"
                      checked={selectedPlan === 'yearly'}
                      onChange={() => setSelectedPlan('yearly')}
                      className="w-4 h-4 accent-amber-400"
                    />
                  </div>

                  <h3 className="text-base font-bold text-white">الاشتراك السنوي (Yearly Best Value)</h3>
                  <div className="flex items-baseline gap-1.5 pt-1">
                    <span className="text-3xl font-black text-emerald-400 font-mono">$8.99</span>
                    <span className="text-xs text-slate-400">/ سنوياً (يعادل $0.74 شهرياً فقط)</span>
                  </div>

                  <p className="text-xs text-slate-300 leading-relaxed pt-2 border-t border-slate-800">
                    دفع سنوي واحد يوفر عليك 25% مقارنة بالدفع الشهري. استمتع بسنة كاملة من كافة المزايا الراقية دون انقطاع.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openStoreSheet('yearly', false);
                  }}
                  disabled={isProcessing}
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold text-xs transition shadow-md flex items-center justify-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  <span>اشترك سنوياً بـ $8.99 (وفر 25%) 👑</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 8 CORE HIGHLIGHTED PREMIUM FEATURES (المزايا الـ 8 بوضوح) */}
      {/* ========================================================= */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span>مزايا وتين Premium الحصرية (الـ 8 الأساسية)</span>
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              تجربة استثنائية متكاملة صُممت بكل عناية لإثراء محادثاتك وتواجدك الرقمي
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              icon: Crown,
              color: 'text-amber-400',
              bg: 'bg-amber-500/15 border-amber-500/30',
              title: '1. شارة Premium 👑',
              desc: 'شارة تاج ملكية أنيقة بجانب اسمك في الملف الشخصي، المحادثات، وقائمة الدردشات تميزك برقي.',
            },
            {
              icon: Palette,
              color: 'text-purple-400',
              bg: 'bg-purple-500/15 border-purple-500/30',
              title: '2. ثيمات حصرية 🎨',
              desc: '4 ثيمات فاخرة خاصة (الذهب الملكي، السديم الكوني، الزمرد المتوهج، والماس الداكن).',
            },
            {
              icon: Sparkles,
              color: 'text-emerald-400',
              bg: 'bg-emerald-500/15 border-emerald-500/30',
              title: '3. تخصيص الملف الشخصي ✨',
              desc: 'بنرات غلاف مخصصة، تأثيرات توهج راقية لاسمك، وخيارات مظهر جمالية لا محدودة.',
            },
            {
              icon: ShieldCheck,
              color: 'text-teal-400',
              bg: 'bg-teal-500/15 border-teal-500/30',
              title: '4. بدون أي إعلانات 🚫',
              desc: 'تجربة صافية وسريعة 100% بدون أي لافتات رعاية أو إعلانات ترويجية تزعج تركيزك.',
            },
            {
              icon: ImageIcon,
              color: 'text-cyan-400',
              bg: 'bg-cyan-500/15 border-cyan-500/30',
              title: '5. جودة وسائط فائقة 📸',
              desc: 'إرسال ومشاركة الصور ومقاطع الفيديو بدقتها الأصلية الفائقة HD/4K دون أي ضغط خوارزمي.',
            },
            {
              icon: FileUp,
              color: 'text-blue-400',
              bg: 'bg-blue-500/15 border-blue-500/30',
              title: '6. ملفات حتى 500MB 📁',
              desc: 'رفع ومشاركة مستندات وفيديوهات ضخمة بحجم يصل لـ 500MB (مقابل 25MB للمستخدم المجاني).',
            },
            {
              icon: MessageSquareCode,
              color: 'text-pink-400',
              bg: 'bg-pink-500/15 border-pink-500/30',
              title: '7. تخصيص المحادثات 💬',
              desc: 'أنماط فقاعات رسائل حصرية (الذهب الملكي، الزجاج، النيون) مع خلفيات Wallpapers خاصة.',
            },
            {
              icon: Zap,
              color: 'text-amber-400',
              bg: 'bg-amber-500/15 border-amber-500/30',
              title: '8. مزايا مستقبلية ⭐',
              desc: 'بنية متطورة تمنحك أولوية تجربة أدوات وتين الذكية والذكاء الاصطناعي فور صدورها.',
            },
          ].map((feat, idx) => {
            const Icon = feat.icon;
            return (
              <div
                key={idx}
                className="p-4 rounded-2xl bg-[#0e1422] border border-slate-800/80 hover:border-slate-700 transition space-y-2 group"
              >
                <div className={`w-9 h-9 rounded-xl ${feat.bg} border flex items-center justify-center ${feat.color} shadow-sm`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-xs font-bold text-white group-hover:text-amber-300 transition">
                  {feat.title}
                </h3>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  {feat.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================= */}
      {/* TRANSPARENT COMPARISON TABLE (FREE VS PREMIUM)            */}
      {/* ========================================================= */}
      <div className="p-6 rounded-3xl bg-[#0e1422] border border-slate-800 space-y-4 shadow-xl">
        <div className="pb-3 border-b border-slate-800">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400" />
            <span>مقارنة شفافة بين وتين المجاني ووتين Premium</span>
          </h2>
          <p className="text-xs text-slate-400">
            تأكيد دائم: المراسلة الأساسية مجانية بالكامل ولن تكون مدفوعة أبداً
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-right text-xs">
            <thead>
              <tr className="text-slate-400 border-b border-slate-800/80">
                <th className="py-2.5 px-3 font-bold">الميزة / الوظيفة</th>
                <th className="py-2.5 px-3 font-bold text-emerald-400">وتين الأساسي (مجاني 100%)</th>
                <th className="py-2.5 px-3 font-bold text-amber-400">وتين Premium 👑</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">المراسلة النصية والمحادثات</td>
                <td className="py-2.5 px-3 text-emerald-400 font-bold">✓ مجاني تماماً ودائم</td>
                <td className="py-2.5 px-3 text-amber-400 font-bold">✓ مجاني تماماً ودائم</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">إنشاء المجموعات والقنوات</td>
                <td className="py-2.5 px-3 text-emerald-400 font-bold">✓ نعم</td>
                <td className="py-2.5 px-3 text-amber-400 font-bold">✓ نعم</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">المكالمات الصوتية والمرئية</td>
                <td className="py-2.5 px-3 text-emerald-400 font-bold">✓ نعم</td>
                <td className="py-2.5 px-3 text-amber-400 font-bold">✓ نعم</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">الحد الأقصى لحجم الملفات</td>
                <td className="py-2.5 px-3 text-slate-400">25 ميغابايت (MB)</td>
                <td className="py-2.5 px-3 text-amber-400 font-black">500 ميغابايت (MB) 👑</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">جودة رفع الصور والفيديو</td>
                <td className="py-2.5 px-3 text-slate-400">جودة قياسية مضغوطة</td>
                <td className="py-2.5 px-3 text-amber-400 font-black">دقة أصلية فائقة HD/4K 📸</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">شارة التاج الملكية 👑</td>
                <td className="py-2.5 px-3 text-slate-500">✕</td>
                <td className="py-2.5 px-3 text-amber-400 font-bold">✓ شارة حصرية بجانب اسمك</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">الثيمات الحصرية الفاخرة</td>
                <td className="py-2.5 px-3 text-slate-500">الثيمات القياسية</td>
                <td className="py-2.5 px-3 text-amber-400 font-bold">✓ 4 ثيمات ملكية خاصة</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">أنماط فقاعات الرسائل</td>
                <td className="py-2.5 px-3 text-slate-500">النمط الافتراضي</td>
                <td className="py-2.5 px-3 text-amber-400 font-bold">✓ 5 أنماط فاخرة قابلة للتخصيص</td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium text-white">الإعلانات واللافتات</td>
                <td className="py-2.5 px-3 text-slate-400">قد تظهر بطاقات خفيفة</td>
                <td className="py-2.5 px-3 text-emerald-400 font-bold">خالي 100% من أي إعلانات 🚫</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ========================================================= */}
      {/* OFFICIAL PLATFORM DISCLOSURES & STORE POLICIES            */}
      {/* ========================================================= */}
      <div className="p-5 rounded-3xl bg-[#0a0f1a] border border-slate-800/80 space-y-3 text-xs text-slate-400 leading-relaxed">
        <div className="flex items-center gap-2 text-slate-300 font-bold">
          <HelpCircle className="w-4 h-4 text-emerald-400" />
          <span>سياسات الدفع الرسمية للمنصات والمتاجر وحماية المشتركين</span>
        </div>
        <ul className="space-y-1.5 list-disc list-inside text-[11px] text-slate-400">
          <li>
            <strong className="text-slate-300">الاشتراك اختياري بالكامل:</strong> المراسلة الأساسية على وتين مجانية 100% لجميع المستخدمين بدون أي قيود.
          </li>
          <li>
            <strong className="text-slate-300">الفترة التجريبية المجانية:</strong> يحصل المشترك الجديد على أول شهر مجاني بالكامل. بعد انتهاء الـ 30 يوماً، يتحول الاشتراك تلقائياً إلى الاشتراك الشهري ($0.99) ما لم يتم إلغاؤه قبل انتهاء الفترة بـ 24 ساعة.
          </li>
          <li>
            <strong className="text-slate-300">إدارة وإلغاء الاشتراك:</strong> يمكنك إلغاء التجديد التلقائي في أي وقت من خلال إعدادات المتجر (App Store أو Google Play) أو مباشرة من هذا القسم داخل وتين، مع استمرار الاستفادة من المزايا حتى نهاية الفترة المدفوعة.
          </li>
          <li>
            <strong className="text-slate-300">حماية البيانات:</strong> عند انتهاء أو إلغاء الاشتراك، لا يتم حذف حسابك ولا محادثاتك ولا رسائلك نهائياً.
          </li>
        </ul>
      </div>

      {/* ========================================================= */}
      {/* MODAL: CONFIRM CANCEL AUTO-RENEWAL                        */}
      {/* ========================================================= */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-md bg-[#0e1422] border border-slate-800 rounded-3xl p-6 space-y-4 shadow-2xl">
            <div className="flex items-center gap-2 text-rose-400">
              <Clock className="w-5 h-5" />
              <h3 className="text-base font-bold text-white">تأكيد إيقاف التجديد التلقائي</h3>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              عند إيقاف التجديد التلقائي، <strong className="text-white">ستستمر كافة مزايا Premium حتى تاريخ {formatDate(sub?.expiresAt)}</strong> ولن يتم خصم أي مبالغ منك لاحقاً. ستظل محادثاتك وحسابك كما هي تماماً.
            </p>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-400">سبب الإلغاء (اختياري لتحسين الخدمة):</label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 bg-[#141b27] border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-emerald-500"
              >
                <option value="تجربة الخدمة فقط">كنت أجرب الخدمة فقط</option>
                <option value="السعر">أفضل التوفير حالياً</option>
                <option value="لا أحتاج المزايا الإضافية">المزايا المجانية كافية لاحتياجاتي</option>
                <option value="سبب آخر">سبب آخر</option>
              </select>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white transition"
              >
                تراجع والاحتفاظ بالتجديد
              </button>
              <button
                type="button"
                onClick={handleCancelAutoRenew}
                disabled={isProcessing}
                className="px-4 py-2 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 border border-rose-500/40 text-xs font-bold transition"
              >
                {isProcessing ? 'جاري الإيقاف...' : 'تأكيد إيقاف التجديد التلقائي'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Platform Store Payment Sheet ( Apple Pay, Google Play, Credit/Debit Cards) */}
      <StorePaymentSheet
        isOpen={showStoreSheet}
        onClose={() => setShowStoreSheet(false)}
        currentUser={currentUser}
        plan={sheetPlan}
        isTrial={sheetIsTrial}
        onSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};
